<script setup lang="ts">
/**
 * One conversation with a member, as the portal's chat shows it (apps/portal/src/views/ChatView.vue): the
 * member at the head, then the thread — your messages set apart by the founder's dirty-white edge, theirs
 * plain, a run a message started as a linked card, and a decision the work raised as the inline decision
 * card, in the thread, in the portal's words.
 *
 * Data in (lib/surfaces.ts ChatEntry, shaped after ChatMessageRow), drawing out. The decision's controls
 * come through `decision-actions` (scoped by the decision); the composer through `composer`. `drawn`
 * passes on to every decision card, for a picture of the chat rather than the chat itself.
 *
 * The rest of the slots are for the portal's live thread, which reads the same way (oldest first, the
 * composer below) with its own behaviour: `avatar` and `actions` in the head, `top` under the head,
 * `log-end` at the foot of the scrolling thread (`.zh-chat__log`, exposed as `log`) for what is waiting
 * now, and `message-extra` inside a message, scoped by the entry, for what the member did while replying.
 * A `system` entry is a line from the OS, drawn on its own row as a muted system label rather than a bubble;
 * `system-entry`, scoped by the entry, draws it instead (the portal adds the run's link beside it, #283).
 * A message from a person carrying a `sender` has their avatar beside it, since several people can share one
 * thread; `sender-avatar` (scoped by the sender) draws it, else their picture or initials do.
 * An entry with an id carries it as `data-entry-id`, so the caller can scroll to it. `thinking` ends the
 * thread with the member at work on a reply, until the reply is there to draw instead.
 *
 * A member's reply is drawn as formatted text (ZhMarkdown): the plain answer first, then, when the reply has a
 * Details section (lib/chat-details.ts), its technical proof behind a "Details" toggle (ZhChatDetails), then
 * `message-extra`. `detailsOpen` is every toggle's default, closed when omitted; the portal opens them in Advanced
 * mode. A person's own message is drawn exactly as they typed it (ZhTypedText), its web addresses links. Every link
 * opens in a new tab.
 */
import { computed, reactive, ref } from 'vue'
import { splitChatDetails } from '../lib/chat-details'
import { type ChatEntry, initials, type SurfaceMember } from '../lib/surfaces'
import ZhChatDetails from './ZhChatDetails.vue'
import ZhDecisionCard from './ZhDecisionCard.vue'
import ZhLayerIcon from './ZhLayerIcon.vue'
import ZhMarkdown from './ZhMarkdown.vue'
import ZhSystemLabel from './ZhSystemLabel.vue'
import ZhTypedText from './ZhTypedText.vue'

const props = defineProps<{
    member: SurfaceMember
    role: string
    entries: readonly ChatEntry[]
    drawn?: boolean
    thinking?: boolean
    /** Whether a reply's Details toggle starts open. A change reaches every toggle, over any the reader moved by hand. */
    detailsOpen?: boolean
}>()

/** A member's reply as the bubble draws it: the answer (null when there is none), then the details (null when none). */
type Reply = { answer: string | null; details: string | null }

/** Each entry beside its reply, when it is one: the reply is split once, not on every draw. */
const rows = computed(() =>
    props.entries.map(entry => {
        if (entry.kind !== 'assistant') {
            return { entry, reply: null }
        }
        const { answer, details } = splitChatDetails(entry.text)
        const reply: Reply = { answer: answer.trim() ? answer : null, details }
        return { entry, reply }
    }),
)

const log = ref<HTMLElement | null>(null)
defineExpose({ log })

/** Pictures that would not load: their sender is drawn by initials instead of a broken image. */
const unloaded = reactive(new Set<string>())

function unload(entry: ChatEntry): void {
    if (entry.kind === 'user' && entry.sender?.avatarUrl) {
        unloaded.add(entry.sender.avatarUrl)
    }
}
</script>

<template>
    <div class="zh-chat">
        <div class="zh-chat__head">
            <span class="zh-chat__avatar" aria-hidden="true"><slot name="avatar">{{ initials(member.name) }}</slot></span>
            <span class="zh-chat__who">{{ member.name }} <span class="zh-chat__label">{{ role }}</span></span>
            <span v-if="$slots.actions" class="zh-chat__actions"><slot name="actions" /></span>
        </div>
        <slot name="top" />
        <div ref="log" class="zh-chat__log">
        <ol class="zh-chat__messages" :aria-label="`Chat with ${member.name}`">
            <li
                v-for="({ entry, reply }, i) in rows"
                :key="entry.id ?? i"
                class="zh-chat__msg"
                :data-kind="entry.kind"
                :data-entry-id="entry.id"
            >
                <template v-if="entry.kind === 'user' || entry.kind === 'assistant'">
                    <span class="zh-chat__sr">{{ entry.kind === 'user' ? (entry.sender?.name ?? 'You') : member.name }}: </span>
                    <article v-if="entry.kind !== 'user' || reply || entry.text || $slots['message-extra']" class="zh-chat__bubble">
                        <template v-if="reply">
                            <ZhMarkdown v-if="reply.answer !== null" class="zh-chat__text" :text="reply.answer" />
                            <ZhChatDetails v-if="reply.details !== null" :text="reply.details" :open="detailsOpen" />
                        </template>
                        <p v-else-if="entry.text" class="zh-chat__text"><ZhTypedText :text="entry.text" /></p>
                        <slot name="message-extra" :entry="entry" :index="i" />
                    </article>
                    <span v-if="entry.kind === 'user' && entry.sender" class="zh-chat__avatar zh-chat__sender" aria-hidden="true">
                        <slot name="sender-avatar" :sender="entry.sender">
                            <img
                                v-if="entry.sender.avatarUrl && !unloaded.has(entry.sender.avatarUrl)"
                                :src="entry.sender.avatarUrl"
                                alt=""
                                :title="entry.sender.name"
                                @error="unload(entry)"
                            />
                            <span v-else :title="entry.sender.name">{{ initials(entry.sender.name) }}</span>
                        </slot>
                    </span>
                </template>
                <div v-else-if="entry.kind === 'run'" class="zh-chat__run">
                    <ZhLayerIcon shape="square" />
                    <span class="zh-chat__run-text">
                        <span class="zh-chat__label">Run started</span>
                        <span class="zh-chat__run-task">{{ entry.task }}</span>
                        <span class="zh-chat__label">{{ entry.who }}</span>
                    </span>
                    <span class="zh-chat__open" aria-hidden="true">Open →</span>
                </div>
                <div v-else-if="entry.kind === 'system'" class="zh-chat__system">
                    <slot name="system-entry" :entry="entry" :index="i">
                        <ZhSystemLabel as="span" tone="muted">{{ entry.text }}</ZhSystemLabel>
                    </slot>
                </div>
                <ZhDecisionCard v-else :decision="entry.decision" variant="inline" :drawn="drawn">
                    <template v-if="$slots['decision-actions']" #actions>
                        <slot name="decision-actions" :decision="entry.decision" />
                    </template>
                </ZhDecisionCard>
            </li>
            <li v-if="thinking" class="zh-chat__msg" data-kind="thinking">
                <span class="zh-chat__sr">{{ member.name }}: </span>
                <div class="zh-chat__bubble zh-chat__thinking" role="status">
                    <span class="zh-chat__dots" aria-hidden="true"><span /><span /><span /></span>
                    <span class="zh-chat__label">Thinking</span>
                </div>
            </li>
        </ol>
        <slot name="log-end" />
        </div>
        <slot name="composer" />
    </div>
</template>

<style scoped>
p,
ol {
    margin: 0;
}

ol {
    padding: 0;
    list-style: none;
}

/* A line from the OS (#283): its own row, labels in a line, not a bubble. */
.zh-chat__system {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.5rem;
    padding: 0.25rem 0;
}

/* The system label (BG §19.3). */
.zh-chat__label {
    margin: 0;
    color: var(--ink-3);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.4;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
}

.zh-chat__sr {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
}

/*
 * The thread's own box holds each message's screen-reader label. Without it, a label (absolute) is placed against
 * whatever positioned box sits further out, escapes the thread's scroll, and stretches the page under it by one
 * label per message: a long chat grew a screen's worth of empty page below its composer.
 */
.zh-chat__log {
    position: relative;
}

/* A member's avatar: their initials in a circle, the brand's shape for org. */
.zh-chat__avatar {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 2.25rem;
    height: 2.25rem;
    overflow: hidden;
    border: 1.5px solid var(--line-strong);
    border-radius: 50%;
    background: var(--void-black);
    color: var(--ink-1);
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    font-weight: 500;
    letter-spacing: var(--tracking-system);
}

.zh-chat__head {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--hair);
}

.zh-chat__actions {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 0.5rem;
    margin-left: auto;
}

.zh-chat__who {
    color: var(--ink-1);
    font-weight: 600;
}

.zh-chat__who .zh-chat__label {
    margin-left: 0.375rem;
}

.zh-chat__messages {
    display: grid;
    gap: 0.875rem;
    padding-block: 1.25rem;
}

.zh-chat__msg {
    display: flex;
}

/* Your messages at the far edge; who wrote one, where the thread knows, beside it at its first line. */
.zh-chat__msg[data-kind="user"] {
    align-items: flex-start;
    justify-content: flex-end;
    gap: 0.5rem;
}

/*
 * 34rem on a wide column, never wider than the column itself on a narrow one. A page that gives the thread its
 * whole column (the portal's Chat) widens the measure with `--zh-chat-bubble-max`. A long unbroken token (a URL,
 * an execution id, a tool payload) breaks inside the bubble rather than pushing the page sideways. `anywhere`,
 * not `break-word`, so the break also counts toward the bubble's min-content width and a flex or grid parent
 * can shrink it.
 */
.zh-chat__bubble {
    min-inline-size: 0;
    max-inline-size: min(var(--zh-chat-bubble-max, 34rem), 100%);
    overflow-wrap: anywhere;
    padding: 0.75rem 1rem;
    border: 1px solid var(--line);
    background: var(--void-black);
    color: var(--ink-1);
    font-size: 1rem;
    line-height: 1.45;
}

/*
 * What a person wrote, as they wrote it: line breaks kept. A member's reply is formatted text instead (ZhMarkdown),
 * which keeps its own line breaks.
 */
.zh-chat__msg[data-kind="user"] .zh-chat__text {
    white-space: pre-wrap;
}

/*
 * The member at work on a reply: amber, the colour of something moving (BG §18.4), with the word beside it.
 * The dots take the status blink's rate, a beat apart, so the wave moves without any one of them flashing.
 */
.zh-chat__thinking {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.zh-chat__dots {
    display: flex;
    gap: 0.3125rem;
}

.zh-chat__dots > span {
    width: 0.4375rem;
    height: 0.4375rem;
    border-radius: 50%;
    background: var(--state-active);
    box-shadow: 0 0 0.5rem color-mix(in srgb, var(--state-active) 70%, transparent);
    animation: zh-chat-think var(--motion-status-blink) ease-in-out infinite;
}

.zh-chat__dots > span:nth-child(2) {
    animation-delay: 0.35s;
}

.zh-chat__dots > span:nth-child(3) {
    animation-delay: 0.7s;
}

@keyframes zh-chat-think {
    0%,
    100% {
        opacity: 0.25;
    }
    50% {
        opacity: 1;
    }
}

@media (prefers-reduced-motion: reduce) {
    .zh-chat__dots > span {
        animation: none;
        opacity: 1;
    }
}

/* Who wrote it, a size under the member's at the head: several people can share a thread. */
.zh-chat__sender {
    width: 1.75rem;
    height: 1.75rem;
    font-size: 0.625rem;
}

.zh-chat__sender img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

/* Your messages: the founder's dirty white, set apart by edge rather than fill. */
.zh-chat__msg[data-kind="user"] .zh-chat__bubble {
    border-color: var(--line-strong);
    background: var(--panel-2);
    box-shadow: inset -3px 0 0 var(--dirty-white);
}

.zh-chat__run {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 0.375rem;
    column-gap: 0.75rem;
    align-items: center;
    max-inline-size: 30rem;
    padding: 0.875rem 1rem;
    border: 1px solid var(--line);
    background: var(--panel-2);
}

.zh-chat__run-text {
    display: grid;
    gap: 0.125rem;
}

.zh-chat__run-task {
    color: var(--ink-1);
    font-size: 1rem;
    font-weight: 600;
}

.zh-chat__open {
    color: var(--electric-cyan);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
}

/*
 * Bounded (`class="zh-chat--bounded"`, #244): the thread on a page that scrolls with the document, such as Create
 * task. It takes a box of its own, most of the screen's height and never less than a short conversation needs; the
 * head and the composer stay in view and only the log scrolls, inside the box, never the page under it. The box's
 * size is theme.css's (`--chat-bounded-h`, `--chat-bounded-min-h`); the spacing inside it is the thread's own, as
 * the head and bubble rules above set it, since theme.css has no spacing scale for a component's insides.
 */
.zh-chat--bounded {
    display: flex;
    flex-direction: column;
    height: var(--chat-bounded-h);
    min-height: var(--chat-bounded-min-h);
}

.zh-chat--bounded .zh-chat__head {
    flex-shrink: 0;
    flex-wrap: wrap;
    padding: 0.75rem 1rem;
}

.zh-chat--bounded .zh-chat__log {
    display: grid;
    flex: 1;
    grid-template-columns: minmax(0, 1fr);
    align-content: start;
    gap: 0.75rem;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 1rem;
}
</style>
